/**
 * MDX-lite Markdown renderer for product documentation (FR-HELP-05).
 *
 * Docs ship with the bundle as `web/src/docs/*.md` (coding conventions). The
 * subset rendered here — headings with anchor ids, paragraphs, bullet and
 * ordered lists, tables, fenced code blocks, blockquote callouts, inline code,
 * bold and links — covers the shipped pages and builds React elements instead
 * of injecting HTML, so there is no `dangerouslySetInnerHTML` and no
 * markup-injection surface.
 *
 * Two details matter beyond rendering:
 *   - heading ids come from `extractSections`, the same helper the ToC and the
 *     deep-link handler use, so `#conflict-resolution` always resolves;
 *   - `highlight` wraps every case-insensitive occurrence of the search term in
 *     `<mark>`, which is how docs search highlights matches in the body.
 */
import { Fragment, type ReactNode } from 'react';

import { extractSections } from '../lib/help-content';

const INLINE_PATTERN = /(`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*)/g;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Splits plain text on the search term and wraps the hits in `<mark>`. */
function withHighlight(text: string, highlight: string, keyPrefix: string): ReactNode {
  if (highlight === '' || text === '') return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, 'ig'));
  if (parts.length === 1) return text;
  return parts.map((part, index) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <mark key={`${keyPrefix}-m${index}`}>{part}</mark>
    ) : (
      <Fragment key={`${keyPrefix}-m${index}`}>{part}</Fragment>
    )
  );
}

function renderInline(text: string, keyPrefix: string, highlight: string): ReactNode[] {
  return text.split(INLINE_PATTERN).map((chunk, index) => {
    const key = `${keyPrefix}-${index}`;
    if (chunk.startsWith('`') && chunk.endsWith('`')) {
      return <code key={key}>{chunk.slice(1, -1)}</code>;
    }
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return <strong key={key}>{withHighlight(chunk.slice(2, -2), highlight, key)}</strong>;
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(chunk);
    if (link) {
      const href = link[2] ?? '#';
      const isExternal = href.startsWith('http');
      return (
        <a
          key={key}
          href={href}
          {...(isExternal ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        >
          {link[1]}
        </a>
      );
    }
    return <Fragment key={key}>{withHighlight(chunk, highlight, key)}</Fragment>;
  });
}

type BlockKind = 'heading' | 'p' | 'ul' | 'ol' | 'code' | 'table' | 'quote';

interface Block {
  kind: BlockKind;
  lines: string[];
  /** Heading level 1–6, only set for `heading`. */
  level?: number;
  /** Fenced-code language hint. */
  language?: string;
}

function isTableRow(line: string): boolean {
  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isTableDivider(line: string): boolean {
  return /^\|[\s:|-]+\|$/.test(line.trim());
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());
}

// eslint-disable-next-line complexity -- one linear scan over line kinds; splitting it would spread the block state across helpers.
function toBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let fence: Block | null = null;

  for (const rawLine of source.split('\n')) {
    const line = rawLine.trimEnd();

    const fenceMarker = /^```(\w*)\s*$/.exec(line.trim());
    if (fence !== null) {
      if (fenceMarker) {
        blocks.push(fence);
        fence = null;
      } else fence.lines.push(rawLine);
      continue;
    }
    if (fenceMarker) {
      fence = { kind: 'code', lines: [], language: fenceMarker[1] ?? '' };
      continue;
    }

    if (line.trim() === '') {
      blocks.push({ kind: 'p', lines: [] });
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({
        kind: 'heading',
        level: heading[1]?.length ?? 1,
        lines: [heading[2]?.trim() ?? ''],
      });
      continue;
    }

    const previous = blocks[blocks.length - 1];

    if (isTableRow(line)) {
      if (previous?.kind === 'table') previous.lines.push(line);
      else blocks.push({ kind: 'table', lines: [line] });
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      if (previous?.kind === 'quote') previous.lines.push(quote[1] ?? '');
      else blocks.push({ kind: 'quote', lines: [quote[1] ?? ''] });
      continue;
    }

    const ordered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (ordered) {
      if (previous?.kind === 'ol') previous.lines.push(ordered[1] ?? '');
      else blocks.push({ kind: 'ol', lines: [ordered[1] ?? ''] });
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      if (previous?.kind === 'ul') previous.lines.push(bullet[1] ?? '');
      else blocks.push({ kind: 'ul', lines: [bullet[1] ?? ''] });
      continue;
    }

    if (previous?.kind === 'p' && previous.lines.length > 0) previous.lines.push(line);
    else blocks.push({ kind: 'p', lines: [line] });
  }

  if (fence !== null) blocks.push(fence);
  return blocks.filter((block) => block.lines.length > 0);
}

function TableBlock({
  lines,
  keyPrefix,
  highlight,
}: {
  lines: string[];
  keyPrefix: string;
  highlight: string;
}): JSX.Element {
  const rows = lines.filter((line) => !isTableDivider(line)).map(splitRow);
  const [head, ...body] = rows;
  return (
    <div className="doc-body__table-scroll">
      <table className="doc-body__table">
        {head === undefined ? null : (
          <thead>
            <tr>
              {head.map((cell, index) => (
                <th key={`${keyPrefix}-h${index}`} scope="col">
                  {renderInline(cell, `${keyPrefix}-h${index}`, highlight)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={`${keyPrefix}-r${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${keyPrefix}-r${rowIndex}-c${cellIndex}`}>
                  {renderInline(cell, `${keyPrefix}-r${rowIndex}-c${cellIndex}`, highlight)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface MarkdownViewProps {
  source: string;
  /** Case-insensitive term to wrap in `<mark>`; empty disables highlighting. */
  highlight?: string;
}

export function MarkdownView({ source, highlight = '' }: MarkdownViewProps): JSX.Element {
  // Ids come from the shared extractor, consumed in document order, so they
  // match the ToC and the deep-link target exactly.
  const anchorIds = extractSections(source).map((section) => section.id);
  let headingIndex = 0;
  const term = highlight.trim();

  return (
    <div className="doc-body">
      {toBlocks(source).map((block, index) => {
        const key = `block-${index}`;

        if (block.kind === 'heading') {
          const id = anchorIds[headingIndex++] ?? `heading-${index}`;
          // Demoted one level: the host page already owns the single `<h1>`, so
          // a doc's `#` title renders as `<h2>` and the outline stays legal
          // (axe `page-has-heading-one` / `heading-order`).
          const Tag = `h${Math.min((block.level ?? 1) + 1, 6)}` as 'h1';
          return (
            <Tag key={key} id={id} className="doc-body__heading" tabIndex={-1}>
              {renderInline(block.lines[0] ?? '', key, term)}
            </Tag>
          );
        }
        if (block.kind === 'code') {
          return (
            <pre key={key} className="doc-body__code">
              <code>{block.lines.join('\n')}</code>
            </pre>
          );
        }
        if (block.kind === 'table') {
          return <TableBlock key={key} lines={block.lines} keyPrefix={key} highlight={term} />;
        }
        if (block.kind === 'quote') {
          return (
            <blockquote key={key} className="doc-body__callout">
              {renderInline(block.lines.join(' '), key, term)}
            </blockquote>
          );
        }
        if (block.kind === 'ul' || block.kind === 'ol') {
          const ListTag = block.kind === 'ul' ? 'ul' : 'ol';
          return (
            <ListTag key={key}>
              {block.lines.map((line, lineIndex) => (
                <li key={`${key}-${lineIndex}`}>
                  {renderInline(line, `${key}-${lineIndex}`, term)}
                </li>
              ))}
            </ListTag>
          );
        }
        return <p key={key}>{renderInline(block.lines.join(' '), key, term)}</p>;
      })}
    </div>
  );
}
